import { hasSossaFields, isSossaDataset } from './sossa';
const numericValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const finiteValues = (values) => (
  Array.isArray(values)
    ? values.map(numericValue).filter((value) => value !== null)
    : []
);

const waveformAmplitude = (waveform) => {
  const values = finiteValues(waveform?.amplitude);
  return values.length > 0 ? Math.max(...values) - Math.min(...values) : null;
};

const resolveCuratorRecordingMetadata = (dataset, datasetInfo) => {
  const metadata = dataset?.metadata || {};
  const sampleRateHz = numericValue(
    datasetInfo?.sampleRateHz ??
    datasetInfo?.samplingRate ??
    datasetInfo?.sampling_rate ??
    metadata.sampleRateHz ??
    metadata.samplingRate ??
    metadata.sampling_rate
  ) ?? 30000;
  const declaredDuration = numericValue(
    datasetInfo?.totalDataPoints ??
    datasetInfo?.durationSamples ??
    metadata.durationSamples ??
    metadata.duration_samples ??
    metadata.totalDataPoints
  );
  let maximumSpikeTime = -1;
  (dataset?.clusters || []).forEach((cluster) => {
    finiteValues(cluster?.spikeTimes).forEach((time) => {
      maximumSpikeTime = Math.max(maximumSpikeTime, time);
    });
  });

  return {
    sampleRateHz: Math.max(sampleRateHz, 1),
    durationSamples: declaredDuration !== null
      ? Math.max(declaredDuration, 0)
      : maximumSpikeTime + 1,
  };
};

const buildCuratorClusterStats = (cluster, recordingMetadata) => {
  const metadata = cluster?.metadata || {};
  const spikeTimes = finiteValues(cluster?.spikeTimes).sort((left, right) => left - right);
  const intervals = spikeTimes.slice(1).map((time, index) => time - spikeTimes[index]);
  const refractorySamples = recordingMetadata.sampleRateHz * 0.002;
  const violationCount = intervals.filter((interval) => interval < refractorySamples).length;
  const suppliedAmplitudes = finiteValues(cluster?.spikeAmplitudes);
  const waveformAmplitudes = suppliedAmplitudes.length > 0
    ? []
    : (cluster?.waveforms || []).map(waveformAmplitude).filter((value) => value !== null);
  const amplitudes = suppliedAmplitudes.length > 0 ? suppliedAmplitudes : waveformAmplitudes;
  const durationSeconds = recordingMetadata.durationSamples / recordingMetadata.sampleRateHz;

  return {
    clusterId: cluster.id,
    count: numericValue(cluster?.spikeCount) ?? spikeTimes.length,
    numSpikes: numericValue(cluster?.spikeCount) ?? spikeTimes.length,
    peakChannel: cluster?.primaryChannel ?? null,
    depth: numericValue(metadata.depth),
    firingRateHz: numericValue(metadata.firingRateHz ?? metadata.firing_rate_hz)
      ?? (durationSeconds > 0 && spikeTimes.length > 0
        ? spikeTimes.length / durationSeconds
        : null),
    isiViolationRate: numericValue(metadata.isiViolationRate ?? metadata.isi_violation_rate)
      ?? (spikeTimes.length > 0
        ? (intervals.length > 0 ? violationCount / intervals.length : 0)
        : null),
    meanAmplitude: numericValue(metadata.meanAmplitude ?? metadata.mean_amplitude)
      ?? (amplitudes.length > 0
        ? amplitudes.reduce((sum, value) => sum + value, 0) / amplitudes.length
        : null),
  };
};

export const normalizeCuratorClusterId = (value, fallback) => {
  const resolved = value ?? fallback;
  const number = numericValue(resolved);

  return number !== null && String(resolved).trim() !== ''
    ? number
    : String(resolved);
};

export const reconcileCuratorClusterSelection = (
  selectedClusters = [],
  availableClusterIds = []
) => {
  const availableIds = new Set(availableClusterIds.map(String));
  return selectedClusters.filter((clusterId) => availableIds.has(String(clusterId)));
};

const getDeclaredTimeUnit = (metadata = {}) => (
  metadata?.units?.time ??
  metadata?.timeUnit ??
  metadata?.time_unit ??
  metadata?.spikeTimeUnit ??
  metadata?.spike_time_unit ??
  ''
);

const getTimeScale = (unit, sampleRateHz) => {
  const normalized = String(unit || '').trim().toLowerCase();
  if (['s', 'sec', 'secs', 'second', 'seconds'].includes(normalized)) return sampleRateHz;
  if (['ms', 'msec', 'millisecond', 'milliseconds'].includes(normalized)) return sampleRateHz / 1000;
  return 1;
};

export const normalizeCuratorDatasetTimes = (dataset, datasetInfo = {}) => {
  if (!Array.isArray(dataset?.clusters)) return dataset;

  const sampleRateHz = numericValue(
    datasetInfo?.sampleRateHz ??
    datasetInfo?.samplingRate ??
    datasetInfo?.sampling_rate
  ) ?? 30000;
  const durationSamples = numericValue(datasetInfo?.totalDataPoints);
  const declaredUnit = getDeclaredTimeUnit(dataset.metadata);
  let timeCount = 0;
  let maximumTime = -Infinity;
  let hasSubSampleValues = false;

  dataset.clusters.forEach((cluster) => {
    if (!Array.isArray(cluster?.spikeTimes)) return;

    cluster.spikeTimes.forEach((rawTime) => {
      const time = numericValue(rawTime);
      if (time === null) return;

      timeCount += 1;
      maximumTime = Math.max(maximumTime, time);
      hasSubSampleValues = hasSubSampleValues || !Number.isInteger(time);
    });
  });

  let unit = declaredUnit;
  if (!unit && timeCount > 0 && durationSamples && sampleRateHz > 0) {
    const durationSeconds = durationSamples / sampleRateHz;

    if (
      maximumTime > 0 &&
      maximumTime <= durationSeconds * 1.1 &&
      hasSubSampleValues
    ) {
      unit = 'seconds';
    }
  }

  const scale = getTimeScale(unit, sampleRateHz);
  if (scale === 1) return dataset;

  return {
    ...dataset,
    metadata: {
      ...(dataset.metadata || {}),
      originalTimeUnit: unit,
      timeUnit: 'samples',
      sampleRateHz,
      timeScaleApplied: scale,
    },
    clusters: dataset.clusters.map((cluster) => ({
      ...cluster,
      spikeTimes: (cluster.spikeTimes || [])
        .map((time) => numericValue(time))
        .filter((time) => time !== null)
        .map((time) => Math.round(time * scale)),
    })),
  };
};

export const createDashboardDataFromCuratorDataset = (dataset, datasetInfo = {}) => {
  const sourceClusters = Array.isArray(dataset?.clusters) ? dataset.clusters : [];
  const recordingMetadata = resolveCuratorRecordingMetadata(dataset, datasetInfo);
  const clusterDataClusters = sourceClusters.map((cluster, index) => {
    const clusterId = normalizeCuratorClusterId(cluster?.id, index);
    const spikeTimes = Array.isArray(cluster?.spikeTimes) ? cluster.spikeTimes : [];
    const pointCount = numericValue(cluster?.spikeCount) ?? spikeTimes.length;
    const primaryChannel = cluster?.primaryChannel ?? null;

    return {
      clusterId,
      clusterLabel: `Cluster ${clusterId}`,
      spikeTimes,
      spikeChannels: Array.isArray(cluster?.spikeChannels) && cluster.spikeChannels.length > 0
        ? cluster.spikeChannels
        : spikeTimes.map(() => primaryChannel),
      spikeAmplitudes: Array.isArray(cluster?.spikeAmplitudes)
        ? cluster.spikeAmplitudes
        : [],
      ...(hasSossaFields(cluster) ? {
        best_match: cluster.best_match,
        second_match: cluster.second_match,
        num_fs_spikes: cluster.num_fs_spikes,
      } : {}),
      pointCount,
      channelId: primaryChannel,
      primaryChannel,
      metadata: cluster?.metadata || {},
      points: Array.isArray(cluster?.points) ? cluster.points : [],
    };
  });

  const clusterIds = clusterDataClusters.map((cluster) => cluster.clusterId);
  const clusters = clusterDataClusters.map((cluster) => ({
    id: cluster.clusterId,
    size: cluster.pointCount,
    peakChannel: cluster.primaryChannel,
    depth: cluster.metadata?.depth ?? null,
  }));
  const clusterStats = Object.fromEntries(sourceClusters.map((cluster, index) => {
    const clusterId = normalizeCuratorClusterId(cluster?.id, index);
    return [String(clusterId), buildCuratorClusterStats(
      { ...cluster, id: clusterId },
      recordingMetadata
    )];
  }));

  return {
    clusters,
    clusterStats,
    clusterLookup: new Map(sourceClusters.map((cluster, index) => [
      String(normalizeCuratorClusterId(cluster?.id, index)),
      cluster,
    ])),
    clusterWaveforms: Object.fromEntries(
      sourceClusters
        .filter((cluster) => Array.isArray(cluster?.waveforms) && cluster.waveforms.length > 0)
        .map((cluster, index) => [
          String(normalizeCuratorClusterId(cluster?.id, index)),
          cluster.waveforms,
        ])
    ),
    clusterData: {
      available: clusterDataClusters.length > 0,
      source: 'curator',
      name: dataset?.name || 'Curator cluster file',
      metadata: { ...(dataset?.metadata || {}), ...(isSossaDataset(dataset) ? { algorithmName: 'SOSSA' } : {}) },
      clusters: clusterDataClusters,
      clusterIds,
      numClusters: clusterDataClusters.length,
      totalPoints: clusterDataClusters.reduce((total, cluster) => total + cluster.pointCount, 0),
    },
  };
};

const dot = (left, right) => (
  left.reduce((total, value, index) => total + value * right[index], 0)
);

const normalizeVector = (vector) => {
  const length = Math.sqrt(dot(vector, vector));
  return length > Number.EPSILON
    ? vector.map((value) => value / length)
    : null;
};

const principalComponent = (centeredRows, dimension, previousComponent = null) => {
  let vector = normalizeVector(
    Array.from({ length: dimension }, (_, index) => Math.sin(index + 1) + Math.cos((index + 1) * 0.7))
  );

  if (!vector) return null;

  for (let iteration = 0; iteration < 35; iteration += 1) {
    const next = new Array(dimension).fill(0);
    centeredRows.forEach((row) => {
      const projection = dot(row, vector);
      row.forEach((value, index) => {
        next[index] += value * projection;
      });
    });

    if (previousComponent) {
      const overlap = dot(next, previousComponent);
      previousComponent.forEach((value, index) => {
        next[index] -= overlap * value;
      });
    }

    vector = normalizeVector(next);
    if (!vector) return null;
  }

  return vector;
};

const computeWaveformPcaClusterData = (clusterData, clusterWaveforms) => {
  if (!Array.isArray(clusterData?.clusters)) return clusterData;

  const observations = [];
  clusterData.clusters.forEach((cluster) => {
    if (Array.isArray(cluster.points) && cluster.points.length > 0) return;

    const clusterId = cluster.clusterId ?? cluster.id;
    const waveforms = clusterWaveforms?.[clusterId] || clusterWaveforms?.[String(clusterId)] || [];

    waveforms.forEach((waveform, waveformIndex) => {
      const amplitude = Array.isArray(waveform?.amplitude)
        ? waveform.amplitude.map(Number)
        : [];
      if (amplitude.length < 2 || amplitude.some((value) => !Number.isFinite(value))) return;

      observations.push({
        clusterId,
        waveformIndex,
        spikeIndex: waveform.spikeIndex ?? waveformIndex,
        spikeTime: waveform.time,
        channel: waveform.channel,
        amplitude,
      });
    });
  });

  if (observations.length < 2) return clusterData;

  const dimension = Math.min(...observations.map((observation) => observation.amplitude.length));
  const means = new Array(dimension).fill(0);
  observations.forEach(({ amplitude }) => {
    for (let index = 0; index < dimension; index += 1) means[index] += amplitude[index];
  });
  means.forEach((_, index) => {
    means[index] /= observations.length;
  });

  const centeredRows = observations.map(({ amplitude }) => (
    amplitude.slice(0, dimension).map((value, index) => value - means[index])
  ));
  const firstComponent = principalComponent(centeredRows, dimension);
  if (!firstComponent) return clusterData;

  const secondComponent = principalComponent(centeredRows, dimension, firstComponent);

  const observationsByCluster = new Map();
  observations.forEach((observation, index) => {
    const key = String(observation.clusterId);
    const current = observationsByCluster.get(key) || [];
    current.push({
      ...observation,
      point: [
        dot(centeredRows[index], firstComponent),
        secondComponent ? dot(centeredRows[index], secondComponent) : 0,
      ],
    });
    observationsByCluster.set(key, current);
  });

  return {
    ...clusterData,
    source: 'curator-waveform-pca',
    clusters: clusterData.clusters.map((cluster) => {
      const clusterId = cluster.clusterId ?? cluster.id;
      const embedded = observationsByCluster.get(String(clusterId));
      if (!embedded?.length) return cluster;

      return {
        ...cluster,
        points: embedded.map((observation) => observation.point),
        spikeTimes: embedded.map((observation) => observation.spikeTime),
        spikeChannels: embedded.map((observation) => observation.channel),
        spikeIndices: embedded.map((observation) => observation.spikeIndex),
        spikeIds: embedded.map((observation) => `${clusterId}:${observation.spikeIndex}`),
        pointCount: embedded.length,
      };
    }),
  };
};

const waveformPcaCache = new WeakMap();

export const createWaveformPcaClusterData = (clusterData, clusterWaveforms) => {
  if (
    !clusterData ||
    typeof clusterData !== 'object' ||
    !clusterWaveforms ||
    typeof clusterWaveforms !== 'object'
  ) {
    return computeWaveformPcaClusterData(clusterData, clusterWaveforms);
  }

  let waveformCache = waveformPcaCache.get(clusterData);
  if (!waveformCache) {
    waveformCache = new WeakMap();
    waveformPcaCache.set(clusterData, waveformCache);
  }
  if (waveformCache.has(clusterWaveforms)) return waveformCache.get(clusterWaveforms);

  const result = computeWaveformPcaClusterData(clusterData, clusterWaveforms);
  waveformCache.set(clusterWaveforms, result);
  return result;
};
